import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { ConfirmDialog } from './ConfirmDialog';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/productSegmentationTree';

const STATUS_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'Aktif' },
];
const statusLabel = (value) => {
  const found = STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : (value === null || value === undefined ? '-' : String(value));
};
const statusTone = (value) => (Number(value) === 1 ? 'ok' : 'neutral');

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['Name'];

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

export function ProductSegmentationTreeListPage() {
  usePageMeta('Ürün Segmentasyon Ağacı', 'Ürün segmentasyon ağaçlarını yönetin.');

  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDeleteClick = (id, name) => {
    setDeleteTarget({ id, name });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    const { id, name } = deleteTarget;
    setShowDeleteConfirm(false);

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Silme işlemi başarısız');
      }

      showToast('success', 'Ağaç başarıyla silindi');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      showToast('error', e.message || 'Silme işlemi başarısız');
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'CreatedDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Adı', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('levelCount', {
      header: 'Seviye Sayısı',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('nodeCount', {
      header: 'Toplam Düğüm',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); navigate(`/product-segmentation-tree/${row.original.id}?edit=1`); }}><EditIcon /></button>}
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); navigate(`/product-segmentation-tree/${row.original.id}`); }}><ViewIcon /></button>
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original.id, row.original.name); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
  ], [navigate, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('CreatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'name', headerName: 'Adı' },
        { field: 'levelCount', headerName: 'Seviye Sayısı' },
        { field: 'nodeCount', headerName: 'Toplam Düğüm' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `urun-segmentasyon-agaci_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        actions={canAdd && <button style={newRecordButtonStyle} onClick={() => navigate('/product-segmentation-tree/new')}>+ Yeni Ağaç</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          onRowDoubleClick={() => {}}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ağacı Sil"
        message={deleteTarget ? `"${deleteTarget.name}" segmentasyon ağacı kalıcı olarak silinecektir.` : ''}
        description="Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        isDangerous={true}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
